import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { FC, useState, useEffect } from 'react';
import { useJobseekerAddCertificateMutation, useJobseekerUpdateCertificateMutation } from '@carcareers/store';
import { useAuth } from '@carcareers/hooks';
import { JobseekerCertificate } from '@libs/models';
import { ApiError } from '@libs/types';

interface JobseekerCertificateModalProps {
  type: 'add' | 'edit';
  open: boolean;
  onClose: () => void;
  certificate?: JobseekerCertificate;
}

export const JobseekerCertificateModal: FC<JobseekerCertificateModalProps> = ({ type, open, onClose, certificate }) => {
  const { session } = useAuth();
  const [addCertificate, { isLoading: isAdding }] = useJobseekerAddCertificateMutation();
  const [updateCertificate, { isLoading: isUpdating }] = useJobseekerUpdateCertificateMutation();

  const [formData, setFormData] = useState({
    name: '',
    issuingAuthority: '',
    issueDate: '',
    description: '',
  });

  const [errors, setErrors] = useState<ApiError[]>([]);

  useEffect(() => {
    if (type === 'edit' && certificate) {
      setFormData({
        name: certificate.name || '',
        issuingAuthority: certificate.issuingAuthority || '',
        issueDate: certificate.issueDate ? new Date(certificate.issueDate).toISOString().split('T')[0] : '',
        description: certificate.description || '',
      });
    } else if (type === 'add') {
      setFormData({
        name: '',
        issuingAuthority: '',
        issueDate: '',
        description: '',
      });
    }
    setErrors([]);
  }, [type, certificate, open]);

  const getFieldError = (fieldName: string): string => {
    const fieldError = errors.find(error => error.property === fieldName);
    return fieldError?.errors?.[0] || '';
  };

  const hasFieldError = (fieldName: string): boolean => {
    return !!getFieldError(fieldName);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear errors for this field when user starts typing
    if (hasFieldError(field)) {
      setErrors(prev => prev.filter(error => error.property !== field));
    }
  };

  const handleSubmit = async () => {
    if (!session?.profile?.id) return;

    setErrors([]);

    try {
      if (type === 'add') {
        await addCertificate({
          jobseekerId: session.profile.id,
          name: formData.name,
          issuingAuthority: formData.issuingAuthority,
          ...(formData.issueDate && { issueDate: new Date(formData.issueDate) }),
          description: formData.description,
        }).unwrap();
      } else if (type === 'edit' && certificate) {
        await updateCertificate({
          id: Number(certificate.id),
          jobseekerId: Number(session.profile.id),
          name: formData.name,
          issuingAuthority: formData.issuingAuthority,
          ...(formData.issueDate && { issueDate: new Date(formData.issueDate) }),
          description: formData.description,
        }).unwrap();
      }

      if (type === 'add') {
        setFormData({
          name: '',
          issuingAuthority: '',
          issueDate: '',
          description: '',
        });
      }
      onClose();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error?.data?.errors) {
        setErrors(error.data.errors);
      }
    }
  };

  const isFormValid = formData.name && formData.issuingAuthority;
  const isLoading = isAdding || isUpdating;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 4,
          },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">{type === 'add' ? 'Add Certificate' : 'Edit Certificate'}</Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <TextField
            label="Certificate Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            fullWidth
            required
            placeholder="e.g., AWS Certified Solutions Architect"
            error={hasFieldError('name')}
            helperText={getFieldError('name')}
            sx={{ borderRadius: 4 }}
            InputProps={{
              sx: { borderRadius: 4 },
            }}
          />
          <TextField
            label="Issuing Authority"
            value={formData.issuingAuthority}
            onChange={(e) => handleInputChange('issuingAuthority', e.target.value)}
            fullWidth
            required
            placeholder="e.g., Amazon Web Services"
            error={hasFieldError('issuingAuthority')}
            helperText={getFieldError('issuingAuthority')}
            sx={{ borderRadius: 4 }}
            InputProps={{
              sx: { borderRadius: 4 },
            }}
          />
          <TextField
            label="Issue Date"
            type="date"
            value={formData.issueDate}
            onChange={(e) => handleInputChange('issueDate', e.target.value)}
            fullWidth
            error={hasFieldError('issueDate')}
            helperText={getFieldError('issueDate')}
            sx={{ borderRadius: 4 }}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              sx: { borderRadius: 4 },
            }}
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            fullWidth
            multiline
            rows={4}
            placeholder="Describe what this certificate covers or your achievements"
            error={hasFieldError('description')}
            helperText={getFieldError('description')}
            sx={{ borderRadius: 4 }}
            InputProps={{
              sx: { borderRadius: 4 },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || isLoading}
          sx={{ borderRadius: 2 }}
        >
          {isLoading
            ? type === 'add'
              ? 'Adding...'
              : 'Updating...'
            : type === 'add'
            ? 'Add Certificate'
            : 'Update Certificate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 